// 斷網時的本地 VMS（SPEC #vms-web-offline、kinio/plan#39；app/vms#11）：雲端「設定 › 本地 VMS」用的 app-api 端點與畫面邏輯。
// 形狀照 cloud/platform cmd/app-api/localvms.go（claude/local-vms）。
// 雲端頁不跨來源打 Station（瀏覽器的區網存取權限會擋）：開新分頁到 Station 的 /enroll，自己只輪詢登記請求。
// 所有請求走 SDK 的 kinioFetch：approve / 重設 PIN 回 403 REAUTH_REQUIRED → 重新驗證對話框（AUTH-05）→ 重送。
import { kinioFetch, planeBase } from '@kinio/web-sdk';

/** GET /v1/me/local-vms/stations 的一筆：我經 P1 / P2 / P3 看得到即時的 Station（P4 / P6 不列；offline_allowed = false 的也列）。 */
export interface LocalVmsStation {
  device_ref: string;
  display_name: string;
  home_id: string;
  home_name: string;
  /** https://<code>.<lan_zone>；憑證還沒簽好時 null */
  main_url: string | null;
  /** https://<區網 IP>:8444 */
  fallback_url: string | null;
  /** 家戶所在場域開了「斷網可用」 */
  offline_allowed: boolean;
  /** ready / preparing（憑證準備中） */
  cert_status: string;
  /** 場域的 offline_max_hours（後端目前沒帶；有帶才顯示「最長 N 小時」） */
  offline_max_hours?: number;
}

/** POST /v1/local-vms/enroll-requests 回應（201）。nonce 只回這一次。 */
export interface EnrollRequest {
  request_id: string;
  device_ref: string;
  home_id: string;
  nonce: string;
  expires_at: string;
  /** 憑證還沒簽好時 null */
  enroll_url: string | null;
  poll_interval_ms?: number;
}

export type EnrollStatus = 'waiting_key' | 'key_attached' | 'approved' | 'expired';

/** GET /v1/local-vms/enroll-requests/{id}（只給建立它的登入，其他 404）。 */
export interface EnrollRequestState {
  request_id: string;
  device_ref: string;
  home_id: string;
  status: EnrollStatus;
  expires_at: string;
  /** 主網址那把公鑰：check_code = 核對碼 */
  main: { jkt: string; check_code: string; attached_at?: string } | null;
  /** 備援網址：等待中 / 已加入（只能看） */
  fallback: 'waiting' | 'joined';
}

/** 登記清單的一列（一把公鑰一列：主網址 + 備援 ro=true 同一個 computer_id）。 */
export interface LocalVmsEnrollment {
  enr_id: string;
  /** 同一次登記（= 一台電腦）；撤銷、重設 PIN 都以它為單位 */
  computer_id: string;
  user_id: string;
  home_id: string;
  device_ref: string;
  label: string;
  ro: boolean;
  check_code?: string;
  created_at: string;
  /** 最後更新 = 上次簽發（還沒簽過 = 登記時間） */
  updated_at: string;
  expires_at: string;
  expired: boolean;
  /** 30 天到期前 7 天（提示〔重新登記〕） */
  renew_due: boolean;
  pin_locked: boolean;
  locked_until: string | null;
  /** Station 換 IP、備援網址要重新登記（後端目前沒帶） */
  fallback_needs_update?: boolean;
}

/** 畫面上的一台電腦：主網址那列 + 有沒有備援。 */
export interface LocalVmsComputer {
  id: string;
  /** 撤銷 / 重設 PIN 用的那一筆（主網址那列；沒有主網址就第一列） */
  enr_id: string;
  user_id: string;
  home_id: string;
  device_ref: string;
  label: string;
  has_fallback: boolean;
  updated_at: string;
  expires_at: string;
  expired: boolean;
  renew_due: boolean;
  pin_locked: boolean;
  locked_until: string | null;
  fallback_needs_update: boolean;
}

const base = (api: string) => planeBase(api) + '/v1';
const enc = encodeURIComponent;

export const localVmsApi = {
  stations: (api: string) => kinioFetch<{ stations?: LocalVmsStation[] }>(`${base(api)}/me/local-vms/stations`).then((r) => r?.stations ?? []),
  createRequest: (api: string, deviceRef: string) => kinioFetch<EnrollRequest>(`${base(api)}/local-vms/enroll-requests`, { method: 'POST', body: { device_ref: deviceRef } }),
  getRequest: (api: string, id: string) => kinioFetch<EnrollRequestState>(`${base(api)}/local-vms/enroll-requests/${enc(id)}`),
  approve: (api: string, id: string, body: { label: string; pin: string }) =>
    kinioFetch<{ enrollments: LocalVmsEnrollment[]; fallback: 'none' | 'joined'; delivered: boolean }>(`${base(api)}/local-vms/enroll-requests/${enc(id)}/approve`, { method: 'POST', body }),
  enrollments: (api: string) => kinioFetch<{ enrollments?: LocalVmsEnrollment[] }>(`${base(api)}/me/local-vms/enrollments`).then((r) => r?.enrollments ?? []),
  /** 撤銷這台電腦（主網址 + 備援一起）。 */
  revoke: (api: string, enrId: string) => kinioFetch<{ revoked: string[] }>(`${base(api)}/me/local-vms/enrollments/${enc(enrId)}`, { method: 'DELETE' }),
  /** 重設 PIN（同一台電腦的每一列一起、解鎖）。 */
  resetPin: (api: string, enrId: string, pin: string) => kinioFetch(`${base(api)}/me/local-vms/enrollments/${enc(enrId)}/pin`, { method: 'POST', body: { pin } }),
  /** 家戶 owner / admin：家戶裡所有人的登記。 */
  homeEnrollments: (api: string, homeId: string) =>
    kinioFetch<{ enrollments?: LocalVmsEnrollment[] }>(`${base(api)}/homes/${enc(homeId)}/local-vms/enrollments`).then((r) => r?.enrollments ?? []),
  /** 家戶 owner / admin 撤銷某人的登記（那台電腦的主網址 + 備援）。 */
  homeRevoke: (api: string, homeId: string, enrId: string) =>
    kinioFetch<{ revoked: string[] }>(`${base(api)}/homes/${enc(homeId)}/local-vms/enrollments/${enc(enrId)}`, { method: 'DELETE' }),
};

/** 輪詢間隔與「新分頁沒打開」提示的時間（SPEC 步驟 1）。 */
export const POLL_MS = 2_000;
export const NO_KEYS_HINT_MS = 20_000;

/** 新分頁的網址：nonce 放 fragment（不進任何伺服器的記錄）。後端給了 enroll_url 就用它。 */
export function enrollUrl(mainUrl: string, nonce: string, given?: string | null): string {
  if (given) return given;
  return mainUrl.replace(/\/+$/, '') + '/enroll#n=' + encodeURIComponent(nonce);
}

/** 本地 PIN：剛好 6 位數字。 */
export function validPin(pin: string): boolean {
  return /^\d{6}$/.test(pin);
}

const DAY = 86_400_000;

/** 到期狀態：expired / soon（後端 renew_due：到期前 7 天）/ ok；days = 還剩幾天（無條件進位）。 */
export function expiryState(c: Pick<LocalVmsComputer, 'expires_at' | 'expired' | 'renew_due'>, now = Date.now()): { kind: 'ok' | 'soon' | 'expired'; days: number } {
  const left = Date.parse(c.expires_at) - now;
  if (c.expired || !(left > 0)) return { kind: 'expired', days: 0 };
  return { kind: c.renew_due ? 'soon' : 'ok', days: Math.ceil(left / DAY) };
}

/**
 * 一把公鑰一列 → 一台電腦一列（computer_id）：主網址那列為主、備援併成「含備援網址（只能看）」；
 * 任一列 PIN 已鎖就算鎖（鎖到最晚的那個時間）。同一台 Station 排一起、新的在前。
 */
export function groupComputers(items: LocalVmsEnrollment[]): LocalVmsComputer[] {
  const by = new Map<string, LocalVmsEnrollment[]>();
  for (const e of items) by.set(e.computer_id, [...(by.get(e.computer_id) ?? []), e]);
  const out: LocalVmsComputer[] = [];
  for (const [id, rows] of by) {
    const main = rows.find((r) => !r.ro) ?? rows[0];
    const locks = rows.filter((r) => r.pin_locked && r.locked_until).map((r) => r.locked_until!);
    out.push({
      id,
      enr_id: main.enr_id,
      user_id: main.user_id,
      home_id: main.home_id,
      device_ref: main.device_ref,
      label: main.label,
      has_fallback: rows.some((r) => r.ro),
      updated_at: rows.map((r) => r.updated_at).sort().at(-1)!,
      expires_at: main.expires_at,
      expired: rows.every((r) => r.expired),
      renew_due: main.renew_due,
      pin_locked: rows.some((r) => r.pin_locked),
      locked_until: locks.sort().at(-1) ?? null,
      fallback_needs_update: rows.some((r) => r.fallback_needs_update),
    });
  }
  return out.sort((a, b) => a.device_ref.localeCompare(b.device_ref) || b.updated_at.localeCompare(a.updated_at) || a.label.localeCompare(b.label));
}

/** 這台 Station 能不能在這台電腦登記：場域開了斷網可用、而且主網址已經準備好（憑證簽好）。 */
export function stationState(s: LocalVmsStation): 'ready' | 'not_allowed' | 'not_ready' {
  if (!s.offline_allowed) return 'not_allowed';
  return s.main_url ? 'ready' : 'not_ready';
}

/** 電腦名稱的預設值（可改）：瀏覽器 · 作業系統，例「Chrome · Windows」。 */
export function guessLabel(ua: string): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /CrOS/.test(ua) ? 'ChromeOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' · ');
}
